import { useEffect, useRef, useState } from 'react';
import { parsePhone } from '../utils/phone';

/**
 * The country code shown beside a phone number, kept while the number itself is still empty.
 *
 * A phone is stored as "+<code>-<digits>", and a number with no digits is stored as '' so that a blank
 * phone saves as blank instead of as a bare "+971-". Until a digit is typed the code has nowhere to
 * live in the stored value, and reading it back from '' always gave the default. A country picked
 * before the number was typed therefore snapped straight back to +92, and so did the country of a
 * number whose digits were deleted.
 *
 *   const country = usePhoneCountryCode(formData.phone);
 *   <CountryCodeSelect value={country.code} onChange={(code) => { country.pick(code); save(buildPhone(code, digits)); }} />
 *   <input value={digits} onChange={(e) => { const next = buildPhone(country.code, e.target.value); country.typed(next); save(next); }} />
 *
 * A stored number always wins, so a record opened for editing shows its own country. When the value is
 * emptied from outside the field, as Clear Form does, the code goes back to the default. When the
 * digits are deleted in the field itself, reported through `typed`, the chosen country stays.
 */
export default function usePhoneCountryCode(value, defaultCode = '92') {
  const { countryCode: codeInValue, localDigits } = parsePhone(value || '', defaultCode);
  const hasNumber = localDigits.length > 0;
  const [kept, setKept] = useState(hasNumber ? codeInValue : defaultCode);
  // The value this field's own typing is about to store, so that change can be told apart from one made
  // elsewhere.
  const typedValue = useRef(undefined);

  useEffect(() => {
    const fromTyping = value === typedValue.current;
    typedValue.current = undefined;
    if (hasNumber) setKept(codeInValue);
    else if (!fromTyping) setKept(defaultCode);
    // Only a change of the stored value matters here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return {
    code: hasNumber ? codeInValue : kept,
    pick: setKept,
    // Typing that leaves the value as it was (a stripped leading zero) stores nothing, so it is not
    // remembered either; a stale entry would later mistake an outside reset for typing.
    typed: (next) => {
      typedValue.current = next === (value || '') ? undefined : next;
    },
  };
}
